"use client";

import Title from "antd/lib/typography/Title";
import Paragraph from "antd/lib/typography/Paragraph";
import React, { useId, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import {
  ArrowRightOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  InstagramOutlined,
  SearchOutlined,
  FilterOutlined,
  UnorderedListOutlined,
  PlayCircleFilled,
} from "@ant-design/icons";
import {
  filterStylists,
  filterStylistPhotos,
  isMappableLocation,
  STYLIST_SERVICES,
  type StylistCardData,
  type StylistDirectory,
  type StylistLocation,
} from "@/lib/stylists";
import FullScreenModal from "@/app/components/FullScreenModal";
import LazyVideo from "@/app/components/LazyVideo";
import styles from "./stylists.module.scss";

import { usePhotoEngagement } from "@/lib/hooks/usePhotoEngagement";

import StylistProfileContent from "./StylistProfileContent";

const StylistMap = dynamic(() => import("./StylistMap"), {
  ssr: false,
  loading: () => (
    <div className={styles.mapMessage} role="status">
      Loading map…
    </div>
  ),
});

function Photo({
  src,
  alt,
  className,
}: {
  src?: string;
  alt: string;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  if (!src || failed)
    return (
      <span
        className={`${className ?? ""} ${styles.imageFallback}`}
        aria-label={alt}
      >
        {alt.trim().charAt(0)}
      </span>
    );
  // Openings serves several image hosts; native lazy images also preserve its URLs.
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      className={className}
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

function StylistCard({
  stylist,
  locations,
  active,
  service,
  onHighlight,
  onOpen,
}: {
  stylist: StylistCardData;
  locations: StylistLocation[];
  active: boolean;
  service: string;
  onHighlight: (ids: string[]) => void;
  onOpen: (stylist: StylistCardData, trigger: HTMLButtonElement) => void;
}) {
  const { root } = usePhotoEngagement();
  const photos = filterStylistPhotos(stylist.photos, service).slice(0, 3);
  return (
    <article
      ref={root}
      className={`${styles.card} ${active ? styles.cardActive : ""}`}
      aria-label={stylist.name}
      onMouseEnter={() => onHighlight(stylist.locationIds)}
      onMouseLeave={() => onHighlight([])}
      onFocus={() => onHighlight(stylist.locationIds)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) onHighlight([]);
      }}
    >
      <div className={styles.cardIdentity}>
        <div className={styles.nameRow}>
          <Photo src={stylist.avatar} alt="" className={styles.avatar} />
          <h2>
            <button
              type="button"
              className={styles.cardTrigger}
              aria-label={`Open ${stylist.name}'s profile`}
              aria-haspopup="dialog"
              onClick={(event) => onOpen(stylist, event.currentTarget)}
            >
              {stylist.name}
            </button>
          </h2>
          {stylist.instagram && (
            <a
              className={styles.instagram}
              href={`https://www.instagram.com/${stylist.instagram}/`}
              target="_blank"
              rel="noopener noreferrer"
              title={`@${stylist.instagram}`}
              aria-label={`${stylist.name} on Instagram (opens in a new tab)`}
            >
              <InstagramOutlined aria-hidden="true" />
            </a>
          )}
        </div>
        <p className={styles.cardLocations}>
          {locations.map((location) => location.name).join(" · ")}
        </p>
      </div>
      {photos.length > 0 && (
        <div
          className={styles.photos}
          aria-label={`Selected work by ${stylist.name}`}
        >
          {photos.map((photo) => (
            <div key={photo.src} className={styles.workPhoto} data-work-photo-id={photo.workPhotoId} data-salon-id={photo.salonId}>
              {photo.type === "video" ? (
                <div
                  role="img"
                  aria-label={`Video preview: ${photo.alt}`}
                  className={styles.videoPreview}
                >
                  <LazyVideo
                    url={photo.src}
                    posterLoading="lazy"
                    reducedMotionControls={false}
                    viewportMargin="0px"
                  />
                  <div className={styles.videoBadge} aria-hidden="true">
                    <PlayCircleFilled />
                  </div>
                </div>
              ) : (
                <Photo src={photo.src} alt={photo.alt} />
              )}
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

export default function StylistBrowser({
  stylists,
  locations,
  partial,
}: StylistDirectory) {
  const [view, setView] = useState<"list" | "map">("map");
  const [selectedStylist, setSelectedStylist] =
    useState<StylistCardData | null>(null);
  const profileTrigger = useRef<HTMLButtonElement | null>(null);
  function closeProfile() {
    setSelectedStylist(null);
    requestAnimationFrame(() => profileTrigger.current?.focus());
  }
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filterPanelId = useId();
  const [query, setQuery] = useState("");
  const [locationId, setLocationId] = useState("");
  const [service, setService] = useState("");
  const [highlighted, setHighlighted] = useState<string[]>([]);
  const [hoveredLocation, setHoveredLocation] = useState("");

  const filtered = useMemo(
    () =>
      filterStylists(stylists, { query, locationId, service }),
    [stylists, query, locationId, service],
  );
  const services = STYLIST_SERVICES.filter((item) =>
    stylists.some((stylist) =>
      Object.values(stylist.servicesByLocation).some((ids) =>
        ids.includes(item.id),
      ),
    ),
  );
  const selectedLocation = locations.find(
    (location) => location.id === locationId,
  );
  const pins = useMemo(
    () =>
      locations
        .filter(isMappableLocation)
        .map((location) => ({
          ...location,
          count: filterStylists(stylists, {
            query,
            service,
            locationId: location.id,
          }).length,
        }))
        .filter((location) => location.count > 0),
    [locations, stylists, query, service],
  );
  const hasFilters = Boolean(query || locationId || service);
  const extraFilterCount =
    Number(Boolean(query.trim())) + Number(Boolean(locationId));

  function clearFilters() {
    setQuery("");
    setLocationId("");
    setService("");
  }

  return (
    <main className={styles.browser} data-view={view}>
      <header className={styles.header}>
        <div>
          <Title level={1} className="page-title page-title--visual-h4">
            Team Edition
          </Title>
          <Paragraph className={styles.subtitle}>
            Find your stylist. Explore their work.
          </Paragraph>
        </div>
        <div
          className={styles.viewSwitch}
          role="group"
          aria-label="Browse view"
        >
          <button
            type="button"
            aria-pressed={view === "list"}
            onClick={() => setView("list")}
          >
            <UnorderedListOutlined aria-hidden="true" /> List
          </button>
          <button
            type="button"
            aria-pressed={view === "map"}
            onClick={() => setView("map")}
          >
            <EnvironmentOutlined aria-hidden="true" /> Map
          </button>
        </div>
      </header>

      <div className={styles.toolbar}>
        <div className={styles.filterRow}>
          {services.length > 0 && (
            <div
              className={styles.filterChips}
              role="group"
              aria-label="Filter by service"
            >
              <button
                type="button"
                aria-pressed={!service}
                onClick={() => setService("")}
              >
                All services
              </button>
              {services.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  aria-pressed={service === item.id}
                  onClick={() => setService(service === item.id ? "" : item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            className={styles.filterButton}
            aria-expanded={filtersOpen}
            aria-controls={filterPanelId}
            data-active={extraFilterCount > 0}
            onClick={() => setFiltersOpen(!filtersOpen)}
          >
            <FilterOutlined aria-hidden="true" />
            Filters
            {extraFilterCount > 0 && (
              <span className={styles.filterCount}>{extraFilterCount}</span>
            )}
          </button>
        </div>
        <div
          id={filterPanelId}
          className={styles.searchRow}
          hidden={!filtersOpen}
        >
          <label className={styles.search}>
            <SearchOutlined aria-hidden="true" />
            <input
              type="search"
              aria-label="Search stylists by name or Instagram"
              placeholder="Search name or Instagram"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <label className={styles.locationSelect}>
            <EnvironmentOutlined aria-hidden="true" />
            <select
              aria-label="Location"
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
            >
              <option value="">All locations</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {partial && (
        <p className={styles.notice} role="status">
          Some locations are temporarily unavailable. You can still explore the
          team below.
        </p>
      )}

      <div
        className={`${styles.directoryLayout} ${view === "map" ? styles.mapLayout : ""}`}
      >
        <aside
          className={styles.mapPane}
          aria-label="Find stylists by location"
        >
          <StylistMap
            locations={pins}
            selectedId={locationId}
            highlightedIds={highlighted}
            onSelect={setLocationId}
            onHover={setHoveredLocation}
          />
          <div className={styles.mapCaption}>
            <EnvironmentOutlined aria-hidden="true" /> Select a location to
            meet the team
          </div>
        </aside>
        <section className={styles.results} aria-label="Stylists">
          <div className={styles.resultsHeading}>
            <p role="status" aria-live="polite">
              <strong>{filtered.length}</strong>{" "}
              {filtered.length === 1 ? "stylist" : "stylists"}
              {selectedLocation
                ? ` in ${selectedLocation.name}`
                : " to discover"}
            </p>
            {hasFilters && (
              <button
                type="button"
                className={styles.clearButton}
                onClick={clearFilters}
              >
                Clear filters <CloseOutlined aria-hidden="true" />
              </button>
            )}
          </div>
          {filtered.length > 0 ? (
            <div className={styles.grid}>
              {filtered.map((stylist) => (
                <StylistCard
                  service={service}
                  key={stylist.username}
                  stylist={stylist}
                  locations={locations.filter((location) =>
                    stylist.locationIds.includes(location.id),
                  )}
                  active={Boolean(
                    hoveredLocation &&
                      stylist.locationIds.includes(hoveredLocation),
                  )}
                  onHighlight={setHighlighted}
                  onOpen={(stylist, trigger) => {
                    profileTrigger.current = trigger;
                    setSelectedStylist(stylist);
                  }}
                />
              ))}
            </div>
          ) : (
            <div className={styles.empty}>
              <SearchOutlined aria-hidden="true" />
              <h2>
                {stylists.length
                  ? "Let’s find your stylist"
                  : "The team will be right back"}
              </h2>
              <p>
                {stylists.length
                  ? "No stylists match these filters. Try another name, service, or location."
                  : "We couldn’t load the team right now. Please try again in a moment."}
              </p>
              {hasFilters ? (
                <button type="button" onClick={clearFilters}>
                  Show all stylists <ArrowRightOutlined aria-hidden="true" />
                </button>
              ) : (
                <Link href="/appointment">
                  Explore locations <ArrowRightOutlined aria-hidden="true" />
                </Link>
              )}
            </div>
          )}
        </section>
      </div>
      {selectedStylist && (
        <FullScreenModal
          open
          stableHeight
          onClose={closeProfile}
          ariaLabel={`${selectedStylist.name} profile`}
        >
          <StylistProfileContent
            key={selectedStylist.username}
            stylist={selectedStylist}
            locationNames={locations
              .filter((location) =>
                selectedStylist.locationIds.includes(location.id),
              )
              .map((location) => location.name)}
            onClose={closeProfile}
          />
        </FullScreenModal>
      )}
    </main>
  );
}
